// ChatOverlay.js — the conversation layer, built BOTTOM-UP: always
// anchored just above the chat bar, its height is exactly its content, so
// it hides as little of the queue as the conversation actually needs.
//
// Window: by default only THIS TURN's messages (everything since this
// engagement began) — a long back-and-forth keeps its space, but the next
// engagement starts fresh. Scrolling up past the top blurs in history
// ('full' mode: whole transcript, mini session banner swaps in for the
// timeline). Compress returns to the turn view.
//
// Dismissal: 5s of true quiet with no visible change, then a progress
// line drains over 6s; any interaction refills it. The fade is layered +
// end-weighted: the scrim (dim/blur over only the covered queue slice)
// lightens steadily through the first ~70% while bubbles stay solid; in
// the last ~30% bubble opacity drops fast. Entry/exit are animated.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import ChatMessages from './ChatMessages';
import { T } from './liveTheme';

const HOLD_MS = 5000;
// Drain length scales with how much the agent just said — more to read,
// longer linger. Tuned so hold + drain bottoms out at ~10s total and a
// typical reply dismisses in ~11-12s.
const FADE_BASE_MS = 2500;
const FADE_PER_CHAR_MS = 25;
const FADE_MIN_MS = 5000;
const FADE_MAX_MS = 15000;
const EXIT_MS = 220;
// Covers the full action choreography: duck 550ms + working ~1.4s +
// settle 200ms + card exit 650ms + breathing room.
const SPOTLIGHT_MS = 3300;

// End-weighted curves over fade progress p (1 = held, 0 = gone)
const scrimAlpha = (p) => 0.85 * Math.pow(p, 0.6);
const scrimBlur = (p) => 6 * Math.pow(p, 0.6);
const contentOpacity = (p) => (p > 0.3 ? 1 : Math.max(0, p / 0.3));

const ChatOverlay = () => {
  const { state, actions } = useComprehendLive();
  const scrollRef = useRef(null);
  const lastBusyRef = useRef(0);
  const [progress, setProgress] = useState(1);
  const [closing, setClosing] = useState(false);
  // Duck out of the way while a queue action animates beneath. Timer-
  // driven state (NOT derived from Date.now() in render — React skips
  // re-renders when nothing else changes, which left the duck stuck).
  const [spotlight, setSpotlight] = useState(false);
  useEffect(() => {
    if (!state.queueSpotlightAt) return undefined;
    setSpotlight(true);
    const t = setTimeout(() => setSpotlight(false), SPOTLIGHT_MS);
    return () => clearTimeout(t);
  }, [state.queueSpotlightAt]);

  const history = state.chatOverlayMode === 'full';
  const messages = history
    ? state.chat.slice(-50)
    : state.chat.slice(state.chatTurnStartIndex);
  const last = messages[messages.length - 1];

  const lastAssistant = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant');
  const fadeMs = Math.min(
    FADE_MAX_MS,
    Math.max(
      FADE_MIN_MS,
      FADE_BASE_MS + (lastAssistant?.text?.length || 0) * FADE_PER_CHAR_MS,
    ),
  );

  const busy =
    state.agentStatus === 'listening' ||
    state.agentSpeaking ||
    state.awaitingReply ||
    Boolean(last?.pending);

  // Pin the peek view to the latest message — on (re)open and on new
  // content. Must be a LAYOUT effect (before paint) + a rAF re-scroll:
  // reading scrollHeight in a plain effect lands before the entry
  // animation/text layout finishes, which left the overlay opened scrolled
  // to the TOP (latest message under the fold until you manually scrolled).
  useLayoutEffect(() => {
    if (history) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    el.scrollTop = el.scrollHeight;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [messages.length, last?.text, history]);

  useEffect(() => {
    if (closing) return undefined;
    const tick = setInterval(() => {
      const now = Date.now();
      if (busy) {
        lastBusyRef.current = now; // ref, not store — no write spam
        setProgress(1);
        return;
      }
      const lastActive = Math.max(
        state.chatLastActivityAt,
        lastBusyRef.current,
      );
      const idleFor = now - lastActive;
      if (idleFor <= HOLD_MS) {
        setProgress(1);
      } else {
        const fade = 1 - (idleFor - HOLD_MS) / fadeMs;
        if (fade <= 0) setClosing(true);
        else setProgress(fade);
      }
    }, 100);
    return () => clearInterval(tick);
  }, [busy, state.chatLastActivityAt, closing, fadeMs]);

  // Animated exit, then actually unmount.
  useEffect(() => {
    if (!closing) return undefined;
    const t = setTimeout(() => actions.closeChatOverlay(), EXIT_MS);
    return () => clearTimeout(t);
  }, [closing, actions]);

  const bump = () => actions.bumpChatActivity();

  // Scroll up past the top of this turn = blur in the history. Capture the
  // current scroll height first so the messages on screen stay anchored
  // while older ones materialize above (classic prepend anchoring).
  const prependAnchorRef = useRef(null);
  const onWheel = (e) => {
    bump();
    if (!history && e.deltaY < 0 && (scrollRef.current?.scrollTop ?? 0) <= 0) {
      prependAnchorRef.current = scrollRef.current?.scrollHeight ?? null;
      actions.setChatOverlayMode('full');
    }
  };

  useLayoutEffect(() => {
    if (history && scrollRef.current) {
      const el = scrollRef.current;
      // No anchor means history opened directly (not via scroll-up) —
      // land on the LATEST message, not the top of the transcript.
      el.scrollTop =
        prependAnchorRef.current != null
          ? el.scrollHeight - prependAnchorRef.current
          : el.scrollHeight;
      prependAnchorRef.current = null;
    }
  }, [history]);

  const draining = progress < 0.999;
  const hasContent = messages.length > 0 || state.awaitingReply;
  if (!hasContent && !history) return null; // nothing to say, hide nothing

  return (
    <div
      onPointerDown={bump}
      onPointerMove={bump}
      onWheel={onWheel}
      style={{
        // Bottom-anchored, content-sized: grows upward only as the
        // conversation does. History mode may fill the whole queue area.
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: '100%',
        height: 'auto',
        // Animate the growth into history mode (min-height transitions;
        // height:auto doesn't).
        minHeight: history ? '100%' : '0%',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 6,
        borderRadius: history ? 0 : '10px 10px 0 0',
        overflow: 'hidden',
        animation: closing ? undefined : 'cplRise .18s ease-out',
        transform: closing
          ? 'translateY(12px)'
          : spotlight
            ? 'translateY(85%)'
            : 'translateY(0)',
        opacity: closing ? 0 : spotlight ? 0.25 : 1,
        pointerEvents: spotlight ? 'none' : 'auto',
        transition: `transform .3s ease, opacity .25s ease, min-height .3s ease`,
      }}
    >
      {/* SCRIM — dims/blurs only the queue slice this layer covers */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `rgba(13, 29, 25, ${scrimAlpha(progress).toFixed(3)})`,
          backdropFilter: `blur(${scrimBlur(progress).toFixed(1)}px)`,
          WebkitBackdropFilter: `blur(${scrimBlur(progress).toFixed(1)}px)`,
          borderTop: history ? 'none' : `1px solid ${T.border}`,
          transition: 'background .12s linear',
        }}
      />

      {/* CONTENT — solid until the last 30% of the fade */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          opacity: contentOpacity(progress),
          transition: 'opacity .12s linear',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px 0',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.5px',
              color: T.textFaint,
            }}
          >
            {history ? 'CONVERSATION' : 'scroll ↑ for history'}
          </span>
          {history && (
            <i
              className="fa fa-compress-alt"
              aria-hidden="true"
              onClick={() => actions.setChatOverlayMode('peek')}
              title="Back to this conversation"
              style={{
                marginLeft: 'auto',
                color: T.textSecondary,
                fontSize: 11,
                cursor: 'pointer',
                padding: 4,
              }}
            />
          )}
          <i
            className="fa fa-times"
            aria-hidden="true"
            onClick={() => setClosing(true)}
            title="Hide chat"
            style={{
              marginLeft: history ? 0 : 'auto',
              color: T.textSecondary,
              fontSize: 12,
              cursor: 'pointer',
              padding: 4,
            }}
          />
        </div>

        <div
          ref={scrollRef}
          className="cpl-scroll"
          style={{
            minHeight: 0,
            overflowY: 'auto',
            padding: '4px 12px 8px',
          }}
        >
          <ChatMessages messages={messages} actions={actions} />
          {state.awaitingReply && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: 6,
              }}
            >
              <span
                className="cpl-typing"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: T.surfaceCard,
                  padding: '9px 12px',
                  borderRadius: 10,
                }}
              >
                <span />
                <span />
                <span />
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Countdown line: quietly always present (full while held); after 5s
          of quiet it slowly drains — length scaled to the reply — and any
          interaction refills it. */}
      <div
        style={{
          position: 'relative',
          height: 2,
          flexShrink: 0,
          background: 'rgba(159,232,200,0.10)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
            background: draining ? T.greenBright : 'rgba(107,216,136,0.35)',
            transition: 'width .1s linear, background .3s ease',
          }}
        />
      </div>
    </div>
  );
};

export default ChatOverlay;
